<script lang="ts">
	/**
	 * Which currency the storefront prices in (Design §10, §14).
	 *
	 * The control itself is `CurrencyPicker` — one segmented control, shared with every admin screen
	 * that picks a currency, so the choice is made the same way wherever it is offered. What lives
	 * here is the half that is only true of the storefront: the pin.
	 *
	 * **Disabled once `Customer.defaultCurrency` is pinned.** A customer's currency is fixed on
	 * their first purchase and every later purchase uses it, so a live selector after that point
	 * would offer a choice the API answers with a 409. It renders the pinned currency **alone**
	 * rather than as one lit segment among greyed-out others: a strip of currencies that cannot be
	 * chosen is a menu of refusals, and the customer is not deciding anything any more. The sentence
	 * beneath says why, because a disabled control with no explanation reads as a bug.
	 *
	 * **A store with one currency gets no control at all.** A picker holding a single option is a
	 * decision presented to somebody who has none, and on the storefront it is worse than clutter:
	 * it invites a customer to look for an alternative that does not exist. Most stores sell in one
	 * currency, so this is the common case rather than an edge one.
	 *
	 * The exception is narrow and load-bearing. Disabling a currency stops new purchases in it and
	 * deliberately leaves existing subscriptions billing in it, so a customer can be pinned to a
	 * currency the store no longer offers — and then the line saying what they are billed in is the
	 * only place they could learn it. That case keeps the panel even though there is nothing to
	 * choose, because it has stopped being a control and become a statement.
	 */

	import CurrencyPicker from './CurrencyPicker.svelte';
	import { createEventDispatcher } from 'svelte';
	import type { StorefrontCurrency } from '../AppTypes';

	export let currencies: StorefrontCurrency[] = [];
	/** The selected code, bindable. */
	export let value: string = '';
	/** Non-null once the customer has bought something; locks the control. */
	export let pinnedCurrency: string | null = null;
	export let compact: boolean = false;

	let className: string = '';
	export { className as class };

	const dispatcher = createEventDispatcher();

	$: isPinned = pinnedCurrency !== null;

	/*
	 * The pinned row is looked up rather than assumed to be on offer. Disabling a currency stops
	 * new purchases in it and deliberately leaves existing subscriptions billing in it, so a
	 * customer's pin can name a currency the store no longer offers — and the one place that must
	 * not go blank is the line telling them what they are billed in.
	 */
	$: pinnedRow =
		pinnedCurrency === null
			? undefined
			: (currencies.find(currency => currency.code === pinnedCurrency) ?? {
					code: pinnedCurrency,
					name: pinnedCurrency.toUpperCase(),
					symbol: pinnedCurrency.toUpperCase(),
					minorUnitDigits: 2,
					roundingIncrement: 1,
					displayOrder: 0,
					isDefault: false
				});

	$: offered = pinnedRow === undefined ? currencies : [pinnedRow];

	/**
	 * Whether there is anything here worth showing.
	 *
	 * Either a genuine choice, or a pin that the single offered currency does not already explain.
	 * Both halves are needed: without the first, every single-currency store carries a control with
	 * one option; without the second, a customer billed in a currency the store has since withdrawn
	 * is shown nothing at all about it.
	 */
	$: hasChoice = currencies.length > 1;
	$: pinNeedsExplaining =
		pinnedCurrency !== null &&
		currencies.length === 1 &&
		currencies[0].code !== pinnedCurrency;

	function fnChange(event: CustomEvent<{ value: string }>): void {
		if (isPinned) return;
		const code = event.detail.value;
		value = code;
		dispatcher('change', code);
	}
</script>

{#if hasChoice || pinNeedsExplaining}
	<div class={className}>
		<div class="flex flex-wrap items-center gap-2">
			<span class="card-label shrink-0">
				<i class="fa-solid fa-coins text-[11px] mr-1" />Currency
			</span>
			<CurrencyPicker
				id="storefront-currency"
				label="Currency"
				currencies={offered}
				value={isPinned ? (pinnedCurrency ?? '') : value}
				disabled={isPinned}
				{compact}
				on:change={fnChange}
			/>
		</div>

		{#if isPinned}
			<p class="text-xs text-default mt-1">
				Billed in {pinnedCurrency?.toUpperCase()} since your first purchase.
			</p>
		{/if}
	</div>
{/if}
